/**
 * Schritt 3 des Workload-Steckbriefs (NC-1279): Nutzungserwartung.
 *
 * Dauer und Laufzeitprofil sind bewusst GETRENNTE Fragen — der
 * Validierungsdialog zu it-dev ergab "1–5 Tage, aber 24/7 Lauf". Das sind zwei
 * Angaben, und die zweite hat eine Plattform-Konsequenz.
 */

import React from 'react';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import { WorkloadProfileInput, ProfileFrequency, ProfileRuntimeProfile } from '../../../services/types';
import { ProfileStepProps } from './types';
import { FREQUENCY_LABEL, FREQUENCY_ORDER } from './profileLabels';

export default function ProfileStepUsage({ value, onChange }: ProfileStepProps) {
  const set = (patch: Partial<WorkloadProfileInput['usage']>) =>
    onChange({ ...value, usage: { ...value.usage, ...patch } });

  const num = (raw: string) => Math.max(0, parseInt(raw, 10) || 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Nutzungserwartung</Typography>

      <TextField select label="Häufigkeit" fullWidth
        helperText="Entscheidet mit, ob sich laufende Image-Pflege lohnt."
        value={value.usage.frequency}
        onChange={(e) => set({ frequency: e.target.value as ProfileFrequency })}
      >
        {FREQUENCY_ORDER.map((f) => <MenuItem key={f} value={f}>{FREQUENCY_LABEL[f]}</MenuItem>)}
      </TextField>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField label="Teilnehmer typisch" type="number" fullWidth
          value={value.usage.participantsTypical}
          onChange={(e) => set({ participantsTypical: num(e.target.value) })} />
        <TextField label="Teilnehmer maximal" type="number" fullWidth
          value={value.usage.participantsMax}
          onChange={(e) => set({ participantsMax: num(e.target.value) })} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField label="Dauer in Tagen — von" type="number" fullWidth
          value={value.usage.durationDays.min}
          onChange={(e) => set({ durationDays: { ...value.usage.durationDays, min: num(e.target.value) } })} />
        <TextField label="Dauer in Tagen — bis" type="number" fullWidth
          value={value.usage.durationDays.max}
          onChange={(e) => set({ durationDays: { ...value.usage.durationDays, max: num(e.target.value) } })} />
      </Box>

      <TextField select label="Laufzeitprofil" fullWidth
        helperText="Dürfen die VMs abends abgeschaltet werden, oder müssen sie durchlaufen?"
        value={value.usage.runtimeProfile}
        onChange={(e) => set({ runtimeProfile: e.target.value as ProfileRuntimeProfile })}
      >
        <MenuItem value="office-hours">Abends abschaltbar</MenuItem>
        <MenuItem value="24x7">Muss durchlaufen</MenuItem>
      </TextField>

      {value.usage.runtimeProfile === '24x7' && (
        <Alert severity="warning">
          Die Plattform schaltet Schulungs-VMs derzeit täglich um 19:00 ab. Ein
          durchlaufender Betrieb ist noch nicht umgesetzt — bitte vor der Schulung
          mit dem Betrieb klären.
        </Alert>
      )}
    </Box>
  );
}
